import * as Sentry from '@sentry/react';
import { BaseError } from '@fretgenius/core/errors'; // Adjust import path if needed based on web app structure

// Add sentryEventId as optional context property
type ErrorContext = Record<string, unknown> & { sentryEventId?: string };

export function reportError(error: unknown, context?: ErrorContext): string | undefined {
  let eventId: string | undefined = context?.sentryEventId; // Use existing eventId if provided

  // Always log detailed error to console in development for easier debugging
  if (process.env.NODE_ENV !== 'production') {
    console.error('[Error Reporter]:', error, 'Context:', context);
  }

  // Only report to Sentry if an eventId wasn't already provided (e.g., from ErrorBoundary)
  if (!eventId) {
    try {
      if (error instanceof BaseError) {
        eventId = Sentry.captureException(error, {
          tags: {
            errorCode: error.code, // Use the structured code as a tag
            errorName: error.name,
          },
          extra: {
            ...context, // User-provided context
            details: error.details, // Structured details from the error
          },
        });
      } else if (error instanceof Error) {
        // Standard JavaScript Error
        eventId = Sentry.captureException(error, {
          tags: {
            errorCode: 'UNHANDLED_JS_ERROR',
            errorName: error.name || 'Error',
          },
          extra: { ...context },
        });
      } else {
        // Handle cases where something other than an Error object was thrown/caught
        eventId = Sentry.captureMessage(`Non-Error caught: ${String(error)}`, {
          level: 'error',
          extra: {
            ...context,
            originalValue: error,
          },
          tags: {
            errorCode: 'NON_ERROR_EXCEPTION',
          },
        });
      }
    } catch (reportingError) {
      // If Sentry itself fails, log to console
      console.error('[Sentry Reporting Failed]:', reportingError);
      console.error('[Original Error]:', error);
    }
  } else if (process.env.NODE_ENV !== 'production') {
    console.info(
      `[Error Reporter]: Skipped Sentry report; event already captured (ID: ${eventId})`
    );
  }

  // Return Sentry event ID if available
  return eventId;
}
